import {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AutocompleteOptions } from '@Components/Autocomplete';
import { DateState } from '@Components/DateRangePicker';
import { MenuItem } from '@Components/PopOverMenu';
import ScriptsLayout from '@Components/ScriptsLayout';
import Table, { TableHead } from '@Components/Table';
import { CommonConstants } from '@Constants/index';
import { HeaderCheckboxState, Icon } from '@Typings/global.types';
import {
  getDateFromISODateTimeString,
  getRowsPerPageOptions,
  getSelectFilterValue,
  getSortingIcon,
  SelectOptions,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

import ScriptsTableProps from './ScriptsTable.types';

const ScriptsTable: FunctionComponent<ScriptsTableProps> = (
  props: ScriptsTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const { tableProps, isAdmin, handleBulk, ...resProps } = props;
  const {
    loading,
    rows,
    paginationProps,
    setChecked,
    checked,
    ...resTableProps
  } = tableProps;
  const { count, ...resPaginationProps } = paginationProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectFilterValue, setSelectFilterValue] = useState<
    AutocompleteOptions[]
  >([]);
  const [dateState, setDateState] = useState<[DateState]>([
    {
      startDate: undefined,
      endDate: undefined,
      key: 'selection',
    },
  ]);
  const [showFilterBtn, setShowFilterBtn] = useState<boolean>(false);
  const [headerCheckboxState, setHeaderCheckboxState] =
    useState<HeaderCheckboxState>({ checked: false, indeterminate: false });

  /** EDGE CASE
   * Adds default query params to the url if not added (pageNo, pageSize, order (sort-order)).
   * */
  useEffect(() => {
    const routeParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
        CommonConstants.INITIAL_PAGE_NO,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
        CommonConstants.INITIAL_ROWS_PER_PAGE,
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
        CommonConstants.INITIAL_SORT_ORDER,
      [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
        CommonConstants.INITIAL_SORT_DIRECTION,
    });

    changeRoute(routeParams);
  }, [changeRoute]);

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const nameParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.NAME) || '';
    const typeParam =
      query.getAll(CommonConstants.QUERY_PARAMS.COMMON.TYPE) || [];
    const dateStartParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_START) as string) ||
        ''
    );
    const dateEndParam = getDateFromISODateTimeString(
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.DATE_END) as string) || ''
    );
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    /** SET STATES */
    setPage(pageNo);
    setRowsPerPage(pageSize);
    setSelectFilterValue(getSelectFilterValue(typeParam));
    setSearchValue(`${nameParam}`);
    setDateState((prevState) => [
      {
        ...prevState[0],
        startDate: dateStartParam,
        endDate: dateEndParam,
      },
    ]);
    setShowFilterBtn(
      !!(nameParam || typeParam.length || dateStartParam || dateEndParam)
    );
  }, [setPage, setRowsPerPage]);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** EDGE CASE
   * If the user performs an action and there are no more results to show
   * on the particular page then the user is redirected to the previous valid page.
   *
   * Set the pageNo to the previous valid page.
   */
  useEffect(() => {
    const newPageNo = Math.ceil(count / rowsPerPage);

    if (count && newPageNo < page + 1) {
      const pageNoParam = query.set(
        CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
        newPageNo
      );

      changeRoute(pageNoParam);
    }
  }, [count, page, rowsPerPage, changeRoute]);

  /** PAGINATION HANDLERS --> */
  /** ROWS PER PAGE OPTIONS */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );
    changeRoute(pageParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });
    changeRoute(pageParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** TABLE HEADER CHECKBOX HANDLERS --> */
  /** Removing the non checkable rows from rows to handle header checkbox state accordingly */
  const nonCheckableRows = useMemo(
    () => rows.filter((row) => !row.isNotCheckable),
    [rows]
  );

  /** Clears all checkboxes. */
  const clearSelection = (): void => {
    if (handleBulk) {
      handleBulk(0);
    }
    if (setChecked) {
      setChecked([]);
    }
  };

  /** Selects the checkboxes of current page. */
  const selectPage = (): void => {
    clearSelection();
    setHeaderCheckboxState({ checked: true, indeterminate: false });
    if (setChecked) {
      setChecked(nonCheckableRows.map((row) => row.id));
    }
  };

  /** Selects the first 1000 rows
   * Select all rows if less than 1000
   */
  const select1000 = () => {
    clearSelection();
    if (handleBulk) {
      handleBulk(Math.min(count, CommonConstants.BULK_COUNT));
    }
  };

  /** Table Head Checkbox Handler */
  const headerCheckClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked && !headerCheckboxState.indeterminate) {
      selectPage();
    } else {
      clearSelection();
    }
  };

  /** HANDLES THE TABLE HEADER CHECKBOX STATE (checked and indeterminate) */
  useEffect(() => {
    if (!loading) {
      if (checked?.length && checked.length !== nonCheckableRows.length) {
        setHeaderCheckboxState({ checked: false, indeterminate: true });
      } else if (
        checked?.length &&
        checked.length === nonCheckableRows.length
      ) {
        setHeaderCheckboxState({ checked: true, indeterminate: false });
      } else {
        setHeaderCheckboxState({ checked: false, indeterminate: false });
      }
    }
  }, [checked?.length, nonCheckableRows.length, loading]);

  /** TABLE HEAD CHECKBOX MENU OPTIONS */
  const menuOptions: MenuItem[] = [
    {
      label: t('common.table.Select Page'),
      value: 'page',
      onClick: selectPage,
    },
    {
      label: t('common.table.Select first 1000'),
      value: '1000',
      onClick: select1000,
    },
    {
      label: t('common.table.Clear Selection'),
      value: 'clear',
      onClick: clearSelection,
    },
  ];
  /** <-- TABLE HEADER CHECKBOX HANDLERS */

  /** FILTERS --> */
  /** SELECT FILTER OPTIONS */
  const options = SelectOptions();

  /** ON SEARCH HANDLER */
  const onSearch = (value: string) => {
    const searchParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.NAME]: value,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });
    changeRoute(searchParam);
    // Clear bulk selection (if any) when user applies search filter
    clearSelection();
  };

  /** ON SELECT FILTER CHANGE */
  const onSelectFilterChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: AutocompleteOptions[]
  ): void => {
    const selectValues = value.map(
      (selectValue: AutocompleteOptions) => selectValue.value
    );

    setSelectFilterValue(value);

    const filterParam = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.TYPE]: selectValues,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });

    changeRoute(filterParam);

    // Clear bulk selection (if any) when user applies select filter
    clearSelection();
  };

  /** ON DATE RANGE FILTER SELECT */
  const getDateState = (startDate?: Date, endDate?: Date) => {
    const dateParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]:
        startDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]:
        endDate?.toISOString() || '',
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
    });

    changeRoute(dateParams);
    // Clear bulk selection (if any) when user applies date filter
    clearSelection();
  };

  /** CLEAR FILTER BUTTON CLICK HANDLER */
  const handleClearFilterBtnClick = () => {
    const params = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.TYPE]: [],
      [CommonConstants.QUERY_PARAMS.COMMON.NAME]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.GROUP_NAME]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_START]: '',
      [CommonConstants.QUERY_PARAMS.COMMON.DATE_END]: '',
    });

    changeRoute(params);
  };
  /** <-- FILTERS */

  /** TABLE --> */
  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.ORDER
    ) as string;
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]: order,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    const tableHead: TableHead[] = [
      {
        label: t('common.messages.Script Name'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.NAME),
        width: 28.4,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.NAME),
      },
      {
        label: t('common.table.List Type'),
        icon: getTableHeadIcon(
          CommonConstants.QUERY_PARAMS.COMMON.DISPLAY_TYPE
        ),
        width: 17.8,
        onClick: () =>
          handleTableHeadClick(
            CommonConstants.QUERY_PARAMS.COMMON.DISPLAY_TYPE
          ),
      },
      {
        label: t('common.messages.Group Name'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.GROUP_NAME),
        width: 28.4,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.GROUP_NAME),
      },
      {
        label: t('common.table.Default'),
        icon: getTableHeadIcon(CommonConstants.QUERY_PARAMS.COMMON.DEFAULT),
        width: 17,
        onClick: () =>
          handleTableHeadClick(CommonConstants.QUERY_PARAMS.COMMON.DEFAULT),
      },
      {
        label: t('common.table.Created Date'),
        icon: getTableHeadIcon(CommonConstants.INITIAL_SORT_ORDER),
        width: 23.8,
        align: 'right',
        onClick: () => handleTableHeadClick(CommonConstants.INITIAL_SORT_ORDER),
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];

    /** Removes group name column if user type is not admin */
    if (!isAdmin) {
      tableHead.splice(2, 1);
    }

    /** Removes default column if user type is admin */
    if (isAdmin) {
      tableHead.splice(3, 1);
    }

    return tableHead;
  };

  /** SCRIPTS ACTIVE TABLE COMPONENT */
  const TableComponent = (
    <Table
      loading={loading}
      rows={rows}
      tableHead={getTableHead()}
      checked={checked}
      setChecked={setChecked}
      checkboxMenuProps={{
        menuOptions: !isAdmin ? menuOptions : undefined,
        checkboxProps: {
          checked: headerCheckboxState.checked,
          indeterminate: headerCheckboxState.indeterminate,
          onChange: headerCheckClickHandler,
          disabled: !nonCheckableRows.length,
        },
      }}
      paginationProps={{
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...resPaginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  return (
    <ScriptsLayout
      isAdmin={isAdmin}
      showFilterControls={showFilterBtn}
      clearFilterBtnProps={{
        onClick: handleClearFilterBtnClick,
        disabled: loading,
      }}
      cancelBtnProps={{ onClick: clearSelection, disabled: loading }}
      selectedBtnProps={{ onClick: clearSelection, disabled: loading }}
      searchProps={{
        value: searchValue,
        setValue: setSearchValue,
        placeholder: t('common.tableFilters.Search by name'),
        onSearch,
      }}
      selectFilterProps={{
        options,
        value: selectFilterValue,
        limitTags: CommonConstants.LIMIT_TAGS,
        textFieldProps: {
          label: t('common.tableFilters.Filter by List Type'),
        },
        onChange: onSelectFilterChange,
      }}
      dateRangeProps={{
        dateState,
        setDateState,
        getDateState,
      }}
      table={TableComponent}
      {...resProps}
    />
  );
};

export default ScriptsTable;
